<template>
  <div>
    <v-tabs
      v-model="disableEditing"
      centered
    >
      <v-tab :key="false">
        Edit
      </v-tab>
      <v-tab :key="true">
        Preview
      </v-tab>
    </v-tabs>

    <div class="edit-section py-8 px-8">
      <div style="max-width: 600px; margin: 0 auto;">
        <div
          v-if="! disableEditing"
          class="text-center mb-6"
        >
          <v-btn
            color="primary"
            @click="openModal(MODE_CREATE)"
          >
            Add question
          </v-btn>
        </div>

        <portal-target name="widget" />

        <webcomet-faq
          v-show="! disableEditing"
          :key="`embed-${embedKey}`"
          :faq-id="faqUuid"
          :disable-statistics="true"
          @faqReady="handleFaqReady"
        />

        <webcomet-faq
          v-if="disableEditing"
          :faq-id="faqUuid"
          :disable-statistics="true"
          style="max-width: 600px; margin: 0 auto;"
        />
      </div>
    </div>

    <v-dialog
      v-model="modal.active"
      :eager="true"
      max-width="700"
    >
      <v-card>
        <v-card-title />

        <v-card-text>
          <v-text-field
            ref="questionTitleInput"
            v-model="modal.form.title"
            :autofocus="modal.mode === MODE_CREATE"
            :error-messages="modal.form.errors.title"
            :disabled="modal.form.processing"
            :loading="modal.form.processing"
            label="Enter question title"
            color="primary"
            outlined
          />

          <wysiwyg-editor
            ref="editor"
            v-model="modal.form.body"
            placeholder="Enter an answer"
            :error-messages="modal.form.errors.body"
            :disabled="modal.form.processing"
          />

          <v-btn
            v-if="modal.mode === MODE_EDIT"
            depressed
            color="error"
            class="mt-2"
            :disabled="modal.form.processing"
            :loading="modal.form.processing"
            @click="deleteQuestion"
          >
            <v-icon left>
              mdi-alert
            </v-icon>
            Delete question
          </v-btn>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="grey"
            text
            @click="closeModal"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            text
            :disabled="modal.form.processing"
            :loading="modal.form.processing"
            @click="submitModal"
          >
            <span v-if="modal.mode === MODE_CREATE">
              Submit
            </span>
            <span v-if="modal.mode === MODE_EDIT">
              Save changes
            </span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const MODE_EDIT = 'edit';
const MODE_CREATE = 'create';

import axios from 'axios';
import WysiwygEditor from '@/lite/components/WysiwygEditor';
import Sortable from 'sortablejs';
import { VBtn } from 'vuetify/lib';

export default {
  components: {
    WysiwygEditor,
  },

  props: {
    faqUuid: {
      type: String,
      required: true
    },
    liteSlug: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      MODE_CREATE: MODE_CREATE,
      MODE_EDIT: MODE_EDIT,

      embedKey: 0,

      sortable: null,
      disableEditing: false,
      shadowRoot: null,
      lastEditedQuestionId: null,

      modal: {
        active: false,
        mode: null,
        form: this.$inertia.form({
          id: null,
          title: '',
          body: ''
        })
      },
    }
  },

  watch: {
    'modal.active': function(value) {
      if (value === false) {
        this.resetModalForm();
      }
    }
  },

  methods: {
    openModal(mode) {
      this.modal.active = true;
      this.modal.mode = mode;
    },

    closeModal() {
      this.resetModalForm();
      this.modal.active = false;
    },

    resetModalForm() {
      this.modal.form.reset();
      this.modal.form.clearErrors();
    },

    reloadEmbed() {
      this.embedKey++;
    },

    reloadWidget() {
      window.eventBus.$emit('widget:update');
    },

    resetWidgetToggleLabel() {
      const widget = this.$el.querySelector('webcomet-faq-widget');
      if (widget.classList.contains('has-been-opened')) {
        widget.toggleLabel = '';
      }
    },

    focusFirstInputFieldWithError() {
      if (this.modal.form.errors.title) {
        this.$nextTick(() => this.$refs.questionTitleInput.focus());
      } else if (this.modal.form.errors.body) {
        window.tinyMCE.activeEditor.focus();
      }
    },

    submitModal() {
      let url;
      if (this.modal.mode === MODE_CREATE) {
        url = this.route('question.lite.store', this.liteSlug);
        this.modal.form.post(url, {
          preserveScroll: true,
          onSuccess: () => {
            this.resetModalForm();
            this.closeModal();
            this.resetWidgetToggleLabel();
            this.reloadEmbed();
            this.reloadWidget();

            this.$nextTick(() => this.$refs.questionTitleInput.focus());
          },
          onError: this.focusFirstInputFieldWithError
        });
      } else if (this.modal.mode === MODE_EDIT) {
        const questionId = this.modal.form.id;
        this.lastEditedQuestionId = questionId;
        url = this.route('question.lite.update', { liteSlug: this.liteSlug, question: questionId });
        this.modal.form.patch(url, {
          preserveScroll: true,
          onSuccess: () => {
            this.resetWidgetToggleLabel();
            this.closeModal();
            this.reloadEmbed();
            this.reloadWidget();
          },
          onErrror: this.focusFirstInputFieldWithError
        });
      }
    },

    injectEditButtons() {
      const items = this.shadowRoot.querySelectorAll('.item');

      items.forEach((el) => {
        const editButton = new VBtn({
          propsData: {
            color: 'primary',
            small: true,
          },
        });

        editButton.$slots.default = ['Edit question'];

        editButton.$on(['click'], (e) => {
          const title = el.querySelector('.item__title').innerText;
          const body = el.querySelector('.answer__content').innerHTML;

          this.modal.form.title = title;
          this.modal.form.body = body;
          this.modal.form.id = Number(el.dataset.questionId);

          this.openModal(this.MODE_EDIT)
        });

        editButton.$mount();
        editButton.$el.classList.add('edit-button');
        editButton.$el.style = 'margin-bottom: 2em; margin-left: 2em';

        el.querySelector('.item__answer').appendChild(editButton.$el);
      });
    },

    injectDragHandles() {
      const items = this.shadowRoot.querySelectorAll('.item');

      items.forEach((el) => {
        const wrapper = el.querySelector('.item__question');
        const handle = document.createElement('div');
        handle.classList.add('drag-handle');
        handle.innerHTML = '<i class="fa fa-grip-vertical"></i>';
        wrapper.prepend(handle);
      });
    },

    initDragHandles() {
      const faq = this.$el.querySelector('webcomet-faq');
      const items = faq.shadowRoot.querySelector('.category .items');

      if (items) {
        const htmlEl = document.querySelector('html');
        this.sortable = Sortable.create(items, {
          ghostClass: 'ghost',
          handle: '.drag-handle',
          animation: 150,
          forceFallback: true,
          onChoose: () => htmlEl.classList.add('grabbing'),
          onStart: () => htmlEl.classList.add('grabbing'),
          onUnchoose: () => htmlEl.classList.remove('grabbing'),
          onEnd: () => htmlEl.classList.remove('grabbing'),
          onUpdate: (event) => {
            const order = Array.from(
              event.item.parentNode.querySelectorAll('.item')
            ).map(el => Number(el.dataset.questionId));

            axios.patch(
              this.route('question-order.lite.update', this.liteSlug).toString(),
              { order: order }
            ).then(() => {
              this.resetWidgetToggleLabel();
              this.reloadWidget();
            });
          },
        });
      }
    },

    openLastModifiedQuestion() {
      if (this.modal.active) {
        return;
      }

      if (this.lastEditedQuestionId) {
        this.shadowRoot.querySelector(`#item-${this.lastEditedQuestionId} .item__toggle`).click();
        this.lastEditedQuestionId = null;
      } else if (this.modal.mode === MODE_CREATE) {
        const itemEls = this.shadowRoot.querySelectorAll('.item');
        const questionIds = Array.from(itemEls).map(el => Number(el.dataset.questionId));
        const createdQuestionId = Math.max(...questionIds);
        if (createdQuestionId) {
          this.shadowRoot.querySelector(`#item-${createdQuestionId} .item__toggle`).click();
        }
      }
    },

    handleFaqReady() {
      window.requestAnimationFrame(() => {
        const faq = this.$el.querySelector('webcomet-faq');

        this.shadowRoot = faq.shadowRoot;

        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = '/assets/css/vuetify.min.css';
        this.shadowRoot.appendChild(style);

        faq.injectCss(`
          input[type="text"] {
            background-color: #fff;
          }
          .item__question {
            display: flex;
          }
          .primary {
            background-color: var(--v-primary-base) !important;
            border-color: var(--v-primary-base) !important;
          }
          .drag-handle {
            background: #F5F5F5;
            color: #BDBDBD;
            padding: 0 1em 0 1em;
            display: flex;
            align-items: center;
            cursor: move !important;
            cursor: grab !important;
            cursor: -moz-grab !important;
            cursor: -webkit-grab !important;
          }
          .drag-handle:active {
            background: #EEEEEE;
            cursor: move !important;
            cursor: -webkit-grabbing !important;
            cursor: -moz-grabbing !important;
            cursor: grabbing !important;
          }
          .ghost { background: rgba(189, 189, 189, 0.3); }
          .sortable-chosen .drag-handle {
            background: #EEEEEE;
          }
        `);

        this.injectEditButtons();
        this.injectDragHandles();
        this.initDragHandles();

        this.openLastModifiedQuestion();
      });
    },

    deleteQuestion() {
      if (window.confirm('Are you sure you want to delete this question?')) {
        const url = this.route('question.lite.destroy', { liteSlug: this.liteSlug, question: this.modal.form.id });
        this.modal.form.delete(url, {
          preserveScroll: true,
          onSuccess: () => {
            this.closeModal();
            this.reloadEmbed();
            this.reloadWidget();
          },
        });
      }
    },
  }
}
</script>

<style>
  .edit-section {
    background-image: url('data:image/png;base64,/9j/4AAQSkZJRgABAgAAZABkAAD/7AARRHVja3kAAQAEAAAAUAAA/+4AJkFkb2JlAGTAAAAAAQMAFQQDBgoNAAABpgAAAccAAAIDAAACMP/bAIQAAgICAgICAgICAgMCAgIDBAMCAgMEBQQEBAQEBQYFBQUFBQUGBgcHCAcHBgkJCgoJCQwMDAwMDAwMDAwMDAwMDAEDAwMFBAUJBgYJDQsJCw0PDg4ODg8PDAwMDAwPDwwMDAwMDA8MDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwM/8IAEQgAFAAUAwERAAIRAQMRAf/EAH8AAAMBAQAAAAAAAAAAAAAAAAIDBAEIAQEAAAAAAAAAAAAAAAAAAAAAEAACAwEAAAAAAAAAAAAAAAAAATARMRARAQAAAAAAAAAAAAAAAAAAADASAQAAAAAAAAAAAAAAAAAAADATAAEEAwEAAAAAAAAAAAAAAAEAEDAx8BGxYf/aAAwDAQACEQMRAAAB7kHhgCzCgnBP/9oACAEBAAEFAhYUjC3x6f/aAAgBAgABBQKH/9oACAEDAAEFAof/2gAIAQICBj8CH//aAAgBAwIGPwIf/9oACAEBAQY/Ah//2gAIAQEDAT8hVTeSJJaFL2a5v//aAAgBAgMBPyGH/9oACAEDAwE/IYf/2gAMAwEAAhEDEQAAEBBJBB//2gAIAQEDAT8QXV1sRKOD6CgsQDcXG//aAAgBAgMBPxCH/9oACAEDAwE/EIf/2Q==');
    background-repeat: repeat;
  }

  .tox {
    border-color: rgba(0, 0, 0, 0.38) !important;
    border-radius: 4px !important;
  }

  .has-error .tox-tinymce {
    border: 2px solid var(--v-error-base) !important;
  }
</style>